import type { ReactNode } from "react";
import { Sentry } from "../lib/sentry";

interface SentryBoundaryProps {
  /** Label shown in Sentry breadcrumbs / tags to identify where the error came from. */
  name: string;
  /** Optional custom fallback UI. Defaults to a compact inline error card. */
  fallback?: React.ReactElement | ((props: { error: unknown; resetError: () => void }) => React.ReactElement);
  children: ReactNode;
}

/**
 * Feature-level Sentry error boundary. Catches render errors in a subtree,
 * reports them to Sentry with a `boundary` tag, and shows a compact fallback
 * without crashing the whole page.
 *
 * Use around individual widgets on the fishing report, the map, third-party
 * embeds, or anything that pulls remote data at render time.
 */
export function SentryBoundary({ name, fallback, children }: SentryBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("boundary", name);
      }}
      fallback={
        fallback ??
        (({ resetError }) => (
          <div
            role="alert"
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-foreground"
          >
            <p className="font-medium">This section couldn't load.</p>
            <p className="mt-1 text-muted-foreground">
              We've been notified. Try again in a moment.
            </p>
            <button
              onClick={resetError}
              className="mt-3 inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              Retry
            </button>
          </div>
        ))
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
